import { useParams, Link } from 'react-router-dom';
import { useEffect, useState, useRef, useCallback } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from '@/components/feature/SEO';
import { getComicBySlug, allComics } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [comic, setComic] = useState<Comic | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [visiblePages, setVisiblePages] = useState(3);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setVisiblePages(3);
    if (slug) {
      const found = getComicBySlug(slug);
      setComic(found);
    }
    setLoading(false);
  }, [slug]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && comic && visiblePages < comic.pages.length) {
          setLoadingMore(true);
          setTimeout(() => {
            setVisiblePages((prev) => Math.min(prev + 3, comic.pages.length));
            setLoadingMore(false);
          }, 500);
        }
      },
      { rootMargin: '200px' }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [visiblePages, comic]);

  const scrollToTopFn = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-foreground-500">加载中...</p>
        </div>
      </div>
    );
  }

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading text-foreground-900 mb-2">未找到该漫画</h1>
            <p className="text-sm text-foreground-500 mb-6">该漫画不存在或已被删除</p>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: comic.title,
    author: { '@type': 'Person', name: comic.author },
    description: comic.description,
    datePublished: comic.updateDate,
    image: comic.coverUrl,
    publisher: {
      '@type': 'Organization',
      name: '汗汗漫画',
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hanhancomic.com' },
      { '@type': 'ListItem', position: 2, name: `${comic.category}漫画`, item: `https://hanhancomic.com/category/${comic.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: comic.title },
    ],
  };

  const relatedComics = allComics
    .filter((c) => c.id !== comic.id && c.categorySlug === comic.categorySlug)
    .slice(0, 4);

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: `${comic.category}漫画`, path: `/category/${comic.categorySlug}` },
    { label: comic.title },
  ];

  return (
    <>
      <SEO
        title={`${comic.title} - 汗汗漫画 | 下拉式连续阅读`}
        description={`${comic.title} - ${comic.description.slice(0, 120)}`}
        keywords={`${comic.title},汗汗漫画,${comic.category}漫画,下拉式漫画,在线阅读,免费漫画,${comic.author}`}
        canonicalUrl={`https://hanhancomic.com/article/${comic.slug}`}
        ogType="article"
        ogImage={comic.coverUrl}
        jsonLd={{ ...articleJsonLd, ...breadcrumbJsonLd }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <div className="bg-background-100 pt-20 pb-6 border-b border-background-200">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="mb-4">
                <Breadcrumb items={breadcrumbItems} />
              </div>

              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-shrink-0">
                  <div className="w-32 md:w-40 aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                    <img
                      src={comic.coverUrl}
                      alt={comic.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h1 className="text-xl md:text-2xl font-heading text-foreground-900 mb-2">
                    {comic.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="text-sm text-foreground-500 whitespace-nowrap">{comic.author}</span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300 flex-shrink-0"></span>
                    <Link
                      to={`/category/${comic.categorySlug}`}
                      className="text-sm text-primary-500 hover:text-primary-600 whitespace-nowrap"
                    >
                      {comic.category}
                    </Link>
                    <span className="w-1 h-1 rounded-full bg-foreground-300 flex-shrink-0"></span>
                    <span className="text-sm text-foreground-500 whitespace-nowrap">{comic.status}</span>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="flex items-center gap-1 text-sm text-accent-600 font-semibold whitespace-nowrap">
                      <i className="ri-star-fill w-4 h-4 flex items-center justify-center"></i>
                      {comic.rating}
                    </span>
                    <span className="text-sm text-foreground-500 whitespace-nowrap">
                      <i className="ri-eye-line mr-1 w-4 h-4 inline-flex items-center justify-center"></i>
                      {(comic.views / 10000).toFixed(1)}万 阅读
                    </span>
                    <span className="text-sm text-foreground-500 whitespace-nowrap">
                      更新于 {comic.updateDate}
                    </span>
                  </div>

                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    {comic.description}
                  </p>

                  <div className="flex flex-wrap gap-2">
                    {comic.tags.map((tag) => (
                      <span key={tag} className="px-3 py-1 bg-background-200 text-foreground-600 text-xs rounded-full whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-2xl mx-auto px-4 md:px-6 py-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-heading text-foreground-900 flex items-center gap-2">
                <i className="ri-scroll-to-bottom-line text-primary-500 w-5 h-5 flex items-center justify-center"></i>
                下拉式阅读
              </h2>
              <p className="text-xs text-foreground-500">
                已加载 {visiblePages}/{comic.pages.length} 页
              </p>
            </div>

            <div className="space-y-4">
              {comic.pages.slice(0, visiblePages).map((page) => (
                <div
                  key={page.pageNumber}
                  className="rounded-lg overflow-hidden bg-background-100 shadow-sm"
                >
                  <img
                    src={page.imageUrl}
                    alt={`${comic.title} - 第${page.pageNumber}页`}
                    className="w-full h-auto"
                    title={`${comic.title} - 第${page.pageNumber}页`}
                  />
                  <div className="px-4 py-2 bg-background-50 border-t border-background-200">
                    <span className="text-xs text-foreground-500">
                      第 {page.pageNumber} 页
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {visiblePages < comic.pages.length && (
              <div ref={loadMoreRef} className="py-10 text-center">
                {loadingMore ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-sm text-foreground-500">下拉加载中...</span>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="animate-bounce mb-3">
                      <i className="ri-arrow-down-line text-2xl text-primary-500 w-6 h-6 flex items-center justify-center mx-auto"></i>
                    </div>
                    <p className="text-sm text-foreground-500">继续向下滚动加载更多内容</p>
                  </div>
                )}
              </div>
            )}

            {visiblePages >= comic.pages.length && (
              <div className="py-10 text-center border-t border-background-200 mt-8">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-primary-100">
                  <i className="ri-check-line text-xl text-primary-500"></i>
                </div>
                <p className="text-sm text-foreground-600 font-medium mb-1">已加载全部内容</p>
                <p className="text-xs text-foreground-500 mb-6">共 {comic.pages.length} 页</p>
                <button
                  onClick={scrollToTopFn}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-up-line w-4 h-4 flex items-center justify-center"></i>
                  回到顶部
                </button>
              </div>
            )}
          </div>

          {relatedComics.length > 0 && (
            <section className="py-12 bg-background-100 border-t border-background-200">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <h3 className="text-lg font-heading text-foreground-900 mb-6">
                  相关推荐
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {relatedComics.map((rc) => (
                    <Link
                      key={rc.id}
                      to={`/article/${rc.slug}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="group cursor-pointer"
                    >
                      <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-2">
                        <img
                          src={rc.coverUrl}
                          alt={rc.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                        />
                      </div>
                      <h4 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {rc.title}
                      </h4>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}